/**
 * MarpTemplateSettings — full settings surface for managing custom Marp
 * deck themes (Wave 4, May 2026).
 *
 * Orchestrator only — each section is its own file under ``marp/``:
 *
 *   A. ``CssUploadPanel``       — raw CSS string upload.
 *   B. ``PptxExtractPanel``     — extract palette + fonts from a .pptx.
 *   C. ``OoxmlTemplatePanel``   — upload a graphical compilable template
 *      (``.docx`` / ``.xlsx`` / ``.pptx`` with ``{{name}}`` placeholders).
 *
 * Two cross-cutting blocks (gallery + premium discovery) live above the
 * grid so both flows can sample from them.
 *
 * Routing: mounted at ``/settings/marp-templates`` (TanStack file route
 * under ``_authed/_modal/settings/``).
 */

import { Lightbulb, Palette, Sparkles, Upload } from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

import {
  CompanionHero,
  LearnStyleDropzone,
  SettingsPanel,
  StyleSwatch,
  SurfaceHeader,
  SurfaceLayout,
  cn,
  toast,
} from "@ando/ui";

import type { StylePreset } from "@/features/sources/marp/api";
import { useStyles } from "@/features/sources/marp/useStyles";
import {
  getLearnedStyleIds,
  useLearnStyleController,
} from "@/features/sources/marp/useLearnStyle";

import {
  CssUploadPanel,
  type CssUploadPanelHandle,
} from "./marp/CssUploadPanel";
import { OoxmlTemplatePanel } from "./marp/OoxmlTemplatePanel";
import { PptxExtractPanel } from "./marp/PptxExtractPanel";

export function MarpTemplateSettings(): React.JSX.Element {
  const { i18n } = useLingui();

  // The PPTX extractor lifts its result into the CSS upload form via an
  // imperative ref — keeps the two panels decoupled without prop-drilling.
  const cssPanelRef = React.useRef<CssUploadPanelHandle | null>(null);

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label={i18n._(msg`Studio · Workspace`)}
          title={i18n._(msg`Template Marp personalizzati`)}
          meta={
            <span className="text-ink-3">
              <Trans>
                Carica un foglio di stile CSS oppure estrai automaticamente la
                palette da un file PowerPoint.
              </Trans>
            </span>
          }
        />
      }
      hero={
        <CompanionHero icon={<Upload size={14} strokeWidth={2} />}>
          <Trans>
            I template personalizzati appaiono nel picker del deck Marp accanto
            ai 4 built-in (Editoriale, McKinsey, Sequoia, Keynote). Lo scope
            "Organizzazione" li condivide con tutti i colleghi dello stesso
            tenant.
          </Trans>
        </CompanionHero>
      }
    >
      <div className="flex flex-col gap-6 pt-5">
        <StylesGalleryPanel />
        <DiscoverPremiumTemplatesPanel />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CssUploadPanel handleRef={cssPanelRef} />
          <PptxExtractPanel cssPanelRef={cssPanelRef} />
          <OoxmlTemplatePanel />
        </div>
      </div>
    </SurfaceLayout>
  );
}
MarpTemplateSettings.displayName = "MarpTemplateSettings";

// ── DiscoverPremiumTemplatesPanel ──────────────────────────────────────
//
// Surfaces 4 external template marketplaces (Slidesgo / Envato / SlideModel
// / Behance) so the user can grab a high-quality ``.pptx`` and feed it back
// into Section B's extractor. Pure presentational card list — no state,
// no network. Outbound links open in a new tab with ``noopener noreferrer``
// to avoid window.opener leakage.

interface PremiumTemplateSource {
  /** Brand name — locale-stable, not translated. */
  readonly title: string;
  readonly url: string;
  readonly description: MessageDescriptor;
  readonly chipLabel: MessageDescriptor;
  readonly chipTone: "neutral" | "accent";
}

const PREMIUM_TEMPLATE_SOURCES: ReadonlyArray<PremiumTemplateSource> = [
  {
    title: "Slidesgo Business",
    url: "https://slidesgo.com/business-presentation-templates",
    description: msg`Migliaia di pitch deck business, gratis`,
    chipLabel: msg`Gratis · attribuzione richiesta`,
    chipTone: "neutral",
  },
  {
    title: "Envato Elements",
    url: "https://elements.envato.com/presentations",
    description: msg`Libreria premium illimitata, uso commerciale`,
    chipLabel: msg`Premium · €15/mese`,
    chipTone: "accent",
  },
  {
    title: "SlideModel Pitch",
    url: "https://slidemodel.com/templates/pitch-deck/",
    description: msg`Pitch deck VC, dashboard, SWOT, Gantt`,
    chipLabel: msg`Business · curato`,
    chipTone: "neutral",
  },
  {
    title: "Behance Curated",
    url: "https://www.behance.net/search/projects?search=pitch+deck+template",
    description: msg`Pitch deck design da agenzie premiate`,
    chipLabel: msg`Gratis · curato`,
    chipTone: "neutral",
  },
];

const DiscoverPremiumTemplatesPanel: React.FC = () => {
  const { i18n } = useLingui();
  return (
    <SettingsPanel
      variant="card"
      aria-labelledby="marp-tpl-discover-title"
      icon={Sparkles}
      title={
        <span id="marp-tpl-discover-title">
          <Trans>Scopri template premium</Trans>
        </span>
      }
      description={
        <Trans>
          Trova template pitch-deck di qualità da convertire automaticamente
        </Trans>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {PREMIUM_TEMPLATE_SOURCES.map((source) => (
          <a
            key={source.url}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "group relative flex flex-col gap-1.5 rounded-lg border border-hairline bg-card p-3",
              "transition-all hover:border-ando-orange/30 hover:shadow-sm",
            )}
          >
            <h3 className="pr-5 font-semibold text-[13px] text-ink-1">
              {source.title}
            </h3>
            <p className="text-[11.5px] text-ink-3">
              {i18n._(source.description)}
            </p>
            <span
              className={cn(
                "mt-0.5 inline-flex w-fit items-center rounded-full px-2 py-0.5 font-medium text-[10.5px]",
                source.chipTone === "accent"
                  ? "bg-ando-orange-soft text-ando-orange"
                  : "bg-cream-deep text-ink-3",
              )}
            >
              {i18n._(source.chipLabel)}
            </span>
          </a>
        ))}
      </div>

      <div className="mt-3 flex items-start gap-1.5 border-hairline border-t pt-3 text-[11px] text-ink-3">
        <Lightbulb
          size={13}
          strokeWidth={1.75}
          className="mt-0.5 shrink-0 text-ando-orange"
          aria-hidden
        />
        <p>
          <Trans>
            Scarica il <code className="font-mono text-ink-2">.pptx</code> che
            preferisci, poi caricalo nella sezione sottostante per convertirlo
            nel tuo theme Ando personalizzato.
          </Trans>
        </p>
      </div>
    </SettingsPanel>
  );
};
DiscoverPremiumTemplatesPanel.displayName =
  "MarpTemplateSettings.DiscoverPremiumTemplatesPanel";

// ── StylesGalleryPanel ─────────────────────────────────────────────────
//
// Browse all built-in visual style presets in one place — the same swatch
// previews (DRY: shared ``StyleSwatch`` from ``@ando/ui``) the user sees in
// the document_create proposal picker. Read-only gallery: styles are
// curated built-ins (unlike uploadable templates), so there's nothing to
// manage here beyond discovery. Degrades to a hint when the BE ``/styles``
// surface isn't deployed yet (``useStyles`` returns an empty list on 404).

const STYLE_CATEGORY_LABEL: Record<string, MessageDescriptor> = {
  warm: msg`Caldi`,
  light: msg`Chiari`,
  dark: msg`Scuri`,
  vivid: msg`Vivaci`,
  bw: msg`Bianco e nero`,
  mixed: msg`Misti`,
};

const STYLE_CATEGORY_ORDER: ReadonlyArray<string> = [
  "warm",
  "light",
  "dark",
  "vivid",
  "bw",
  "mixed",
];

const StylesGalleryPanel: React.FC = () => {
  const { i18n } = useLingui();
  const stylesQuery = useStyles();
  const styles: ReadonlyArray<StylePreset> = stylesQuery.data ?? [];

  const learn = useLearnStyleController({
    onLearned: (style) =>
      toast.success(i18n._(msg`Stile "${style.name}" appreso`)),
  });

  // Learned styles (user-uploaded references) surface first under "I tuoi
  // stili"; the curated built-ins follow, grouped by palette family.
  const { learned, grouped } = React.useMemo(() => {
    const learnedIds = getLearnedStyleIds();
    const learnedStyles: StylePreset[] = [];
    const buckets = new Map<string, StylePreset[]>();
    for (const s of styles) {
      if (learnedIds.has(s.id)) {
        learnedStyles.push(s);
        continue;
      }
      const key = STYLE_CATEGORY_ORDER.includes(s.category) ? s.category : "mixed";
      const arr = buckets.get(key) ?? [];
      arr.push(s);
      buckets.set(key, arr);
    }
    return {
      learned: learnedStyles,
      grouped: STYLE_CATEGORY_ORDER.map((cat) => ({
        category: cat,
        items: buckets.get(cat) ?? [],
      })).filter((g) => g.items.length > 0),
    };
    // ``learn.pending`` is a dep so a freshly-learned style re-buckets once it
    // lands in the (invalidated) styles query.
  }, [styles, learn.pending]);

  const renderStyleItem = (s: StylePreset, isLearned: boolean) => (
    <li
      key={s.id}
      className="flex items-center gap-2.5 rounded-lg border border-hairline bg-card/60 px-2.5 py-2"
    >
      <StyleSwatch
        surface={s.surface}
        ink={s.ink}
        accent={s.accent}
        isDark={s.is_dark}
        size={40}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-[12.5px] text-ink-1">{s.name}</p>
        <p className="text-[10.5px] text-ink-3">
          {s.is_dark ? i18n._(msg`Scuro`) : i18n._(msg`Chiaro`)}
        </p>
      </div>
      {isLearned ? (
        <span className="shrink-0 rounded-full border border-ando-orange/30 bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[9.5px] text-ando-orange uppercase tracking-wide">
          <Trans>Tuo</Trans>
        </span>
      ) : null}
    </li>
  );

  return (
    <SettingsPanel
      variant="card"
      aria-labelledby="marp-tpl-styles-title"
      icon={Palette}
      title={
        <span id="marp-tpl-styles-title">
          <Trans>Stili</Trans>
        </span>
      }
      description={
        <Trans>
          Look pronti che l'AI applica a documenti, fogli e presentazioni.
          Sceglili al momento della creazione.
        </Trans>
      }
    >
      <div className="flex flex-col gap-3">
        <LearnStyleDropzone
          onPick={learn.learn}
          pending={learn.pending}
          error={learn.error}
        />

        {stylesQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-hidden>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-[64px] animate-pulse rounded-lg border border-hairline bg-cream-deep/30"
              />
            ))}
          </div>
        ) : styles.length === 0 ? (
          <p className="rounded-md border border-hairline bg-card/40 px-3 py-4 text-center text-[12px] text-ink-3">
            <Trans>Nessuno stile disponibile al momento.</Trans>
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {learned.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <div className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
                  <Trans>I tuoi stili</Trans>
                </div>
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {learned.map((s) => renderStyleItem(s, true))}
                </ul>
              </div>
            ) : null}
            {grouped.map((group) => {
              const label = STYLE_CATEGORY_LABEL[group.category];
              return (
                <div key={group.category} className="flex flex-col gap-1.5">
                  <div className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
                    {label ? i18n._(label) : group.category}
                  </div>
                  <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {group.items.map((s) => renderStyleItem(s, false))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {stylesQuery.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-[11.5px] text-destructive"
          >
            <Trans>Impossibile caricare gli stili — riprova fra un istante.</Trans>
          </p>
        ) : null}
      </div>
    </SettingsPanel>
  );
};
StylesGalleryPanel.displayName = "MarpTemplateSettings.StylesGalleryPanel";
